/**
 * The connector library page's list, as pure functions (no React, no i18n):
 * which rows the search and the column filters keep (item 83), the order,
 * what needs attention, and the words a row's status and sign-in are said in
 * (as translation keys). `tests/unit/connector_library/library_list.test.mjs`
 * runs the real code.
 */

export type Kind = 'mcp' | 'api';
export type UseFilter = 'all' | 'used' | 'unused';

/** The fields of a list row these functions read. */
export interface ListRow {
  entry_id: string;
  kind: Kind;
  name: string;
  description?: string | null;
  owner_name: string | null;
  inherited: boolean;
  edited_here: boolean;
  removed_here: boolean;
  status: 'connected' | 'error' | 'sign_in_needed';
  needs_credential: boolean;
  has_shared_credential: boolean;
  usage: {
    total: number; following: number; customized: number; needs_key?: number;
    /** Organizations whose agents use it, and the first of their names (most agents first). */
    organizations?: number; organization_names?: string[];
  };
  /** Organizations in the managed part of the tree that get it (itself included). */
  available_to?: { organizations: number; total: number; only: boolean };
  /** The organization a row acts at (item 74: under a filter; item 83: always, the owner when it is in view). */
  level?: { tenant_id: string; name: string | null } | null;
  /** Who added it (item 83). */
  owner?: { tenant_id: string; name: string | null; parent_name: string | null };
  signin_mode?: string;
}

export function matches(row: Pick<ListRow, 'name' | 'description'>, query: string): boolean {
  const q = query.trim().toLocaleLowerCase();
  return !q || `${row.name} ${row.description ?? ''}`.toLocaleLowerCase().includes(q);
}

/** The column that sorts: how many agents use a row. */
export type CountColumn = 'agents';
/** The order in force: one count column, most first or fewest first — or none (as listed). */
export type CountSort = { by: CountColumn; dir: 'desc' | 'asc' } | null;

/** A click on a count heading: most first → fewest first → as listed. A
    click on the other heading starts it at most first. */
export function nextSort(sort: CountSort, by: CountColumn): CountSort {
  if (sort?.by !== by) return { by, dir: 'desc' };
  return sort.dir === 'desc' ? { by, dir: 'asc' } : null;
}

/** The aria-sort of a count heading. */
export function ariaSort(sort: CountSort, by: CountColumn): 'descending' | 'ascending' | 'none' {
  return sort?.by !== by ? 'none' : sort.dir === 'desc' ? 'descending' : 'ascending';
}

/** Rows by a count column; equal counts keep the order they came in. */
export function sortByCount<T extends ListRow>(rows: T[], sort: CountSort): T[] {
  if (!sort) return rows;
  const sign = sort.dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => sign * (a.usage.total - b.usage.total));   // stable: ties keep their order
}

/** Every organization in the managed part of the tree gets it. A row it was
    removed from here counts as "some" — that is the point of looking. */
export function everywhere(row: Pick<ListRow, 'available_to' | 'removed_here'>): boolean {
  const a = row.available_to;
  return !row.removed_here && !!a && a.organizations >= a.total;
}

/** The "Available to" cell, as a translation key and its values:
    "All 10" / "3 of 10" / "Removed here". */
export function reachWords(row: Pick<ListRow, 'available_to' | 'removed_here'>):
  { key: string; values: Record<string, number> } {
  const a = row.available_to;
  if (row.removed_here || !a) return { key: 'connectorLibrary.reach.removedHere', values: {} };
  return everywhere(row)
    ? { key: 'connectorLibrary.reach.all', values: { count: a.total } }
    : { key: 'connectorLibrary.reach.some', values: { count: a.organizations, total: a.total } };
}

export type Attention<T extends ListRow = ListRow> =
  | { kind: 'failed'; row: T }
  | { kind: 'needs_key'; row: T; count: number };

/** What the attention strip names — what this organization can act on: a
    failed refresh / read of its OWN entry (only the owner can refresh one),
    and agents that cannot work until someone enters their own key. Removed
    rows never. */
export function attention<T extends ListRow>(rows: T[]): Attention<T>[] {
  const out: Attention<T>[] = [];
  for (const row of rows) {
    if (row.removed_here) continue;
    if (row.status === 'error' && !row.inherited) out.push({ kind: 'failed', row });
    const keys = row.usage.needs_key ?? 0;
    if (keys > 0) out.push({ kind: 'needs_key', row, count: keys });
  }
  return out;
}

/** Whose version "Restore" brings back: the level just above, else the owner. */
export function restoreTarget(row: { parent_name?: string | null; owner_name: string | null }): string {
  return row.parent_name ?? row.owner_name ?? '';
}

/** The status a row shows, if any — at most one, the most urgent. */
export type RowStatus = 'failed' | 'hidden' | 'changed' | 'signin' | null;
export function rowStatus(row: ListRow): RowStatus {
  if (row.removed_here) return 'hidden';
  if (row.status === 'error') return 'failed';
  // Each person signs in, and its tools are not listed yet (item 65A).
  if (row.status === 'sign_in_needed') return 'signin';
  if (row.edited_here) return 'changed';
  return null;
}

/** How it signs in: nothing, each agent's own token, the shared one — or
    each PERSON's own account or key (item 65A). */
export type SignIn = 'none' | 'each' | 'shared' | 'person' | 'personKey';
export function signIn(row: Pick<ListRow, 'needs_credential' | 'has_shared_credential' | 'signin_mode'>): SignIn {
  if (row.signin_mode === 'per_person_oauth') return 'person';
  if (row.signin_mode === 'per_person_key') return 'personKey';
  if (row.has_shared_credential) return 'shared';
  return row.needs_credential ? 'each' : 'none';
}

/** The Agents cell: how many agents in the managed part of the tree use it, or "Not used". */
export function agentsCell(usage: ListRow['usage']): { count: number } | { key: string } {
  return usage.total > 0 ? { count: usage.total } : { key: 'connectorLibrary.usage.notUsed' };
}

/** The Organizations cell: in how many organizations agents use it, the
    names the server sent (most agents first), and how many more there are —
    or "Not used". */
export function organizationsCell(usage: ListRow['usage']):
  { count: number; names: string[]; more: number } | { key: string } {
  const count = usage.organizations ?? 0;
  if (count === 0) return { key: 'connectorLibrary.usage.notUsed' };
  const names = usage.organization_names ?? [];
  return { count, names, more: Math.max(count - names.length, 0) };
}

// ============================================================ column filters (item 83)

/** "Available to" as a filter reads it: every organization, some of them, or removed here. */
export type Reach = 'all' | 'some' | 'removed';
export function reachOf(row: Pick<ListRow, 'available_to' | 'removed_here'>): Reach {
  return row.removed_here ? 'removed' : everywhere(row) ? 'all' : 'some';
}

/** A row's status as a filter reads it: its pill, or none ('ok'). */
export type StatusKey = Exclude<RowStatus, null> | 'ok';
export function statusOf(row: ListRow): StatusKey {
  return rowStatus(row) ?? 'ok';
}

/** What each column keeps (an empty set keeps everything). */
export interface ColumnFilters {
  owner: Set<string>;
  kind: Set<Kind>;
  signIn: Set<SignIn>;
  reach: Set<Reach>;
  use: UseFilter;
  status: Set<StatusKey>;
}

export function noColumnFilters(): ColumnFilters {
  return { owner: new Set(), kind: new Set(), signIn: new Set(), reach: new Set(), use: 'all', status: new Set() };
}

const keeps = <V,>(set: Set<V>, value: V) => set.size === 0 || set.has(value);

/** The rows the search and every column's filter keep. */
export function filterRows<T extends ListRow>(rows: T[], query: string, f: ColumnFilters): T[] {
  return rows.filter(r => matches(r, query)
    && keeps(f.owner, r.owner?.tenant_id ?? '')
    && keeps(f.kind, r.kind)
    && keeps(f.signIn, signIn(r))
    && keeps(f.reach, reachOf(r))
    && (f.use === 'all' || (f.use === 'used' ? r.usage.total > 0 : r.usage.total === 0))
    && keeps(f.status, statusOf(r)));
}

/** A column's choices, from the rows in view: each value once, with how many rows have it. */
export function columnChoices<T extends ListRow, V extends string>(rows: T[], valueOf: (row: T) => V): { value: V; count: number }[] {
  const counts = new Map<V, number>();
  rows.forEach(r => counts.set(valueOf(r), (counts.get(valueOf(r)) ?? 0) + 1));
  return [...counts.entries()].map(([value, count]) => ({ value, count }));
}

/** The owners in view, each once, in the order the rows come (the tree's), with their counts. */
export function ownerChoices<T extends ListRow>(rows: T[]): { id: string; name: string; count: number }[] {
  const out = new Map<string, { id: string; name: string; count: number }>();
  for (const r of rows) {
    const id = r.owner?.tenant_id ?? '';
    const hit = out.get(id) ?? { id, name: r.owner?.name ?? '', count: 0 };
    hit.count += 1;
    out.set(id, hit);
  }
  return [...out.values()];
}
